/**
 * DeltaBadge — Signed score delta badge for analysis history rows.
 *
 * Negative delta = improvement (teal). Positive delta = worsening (amber).
 * Zero delta = unchanged (neutral). isNew = first occurrence (neutral with "new" label).
 *
 * Accessibility: Hidden from a11y tree — parent row provides full context.
 */

import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import type { TFunction } from '@dramadetector/shared';

interface DeltaBadgeProps {
  delta: number;
  isNew?: boolean;
  t: TFunction<'practitioner'>;
}

function getDeltaStyle(delta: number): { bg: string; text: string } {
  if (delta < 0) return { bg: '#CCFBF1', text: '#0D9488' }; // improving — teal
  if (delta > 0) return { bg: '#FEF3C7', text: '#92400E' }; // worsening — amber
  return { bg: '#F3F4F6', text: '#374151' };                 // unchanged — neutral
}

function formatDelta(delta: number): string {
  if (delta < 0) return delta.toString();
  return `+${delta}`;
}

export const DeltaBadge: React.FC<DeltaBadgeProps> = ({ delta, isNew = false, t }) => {
  const style = isNew ? { bg: '#F3F4F6', text: '#374151' } : getDeltaStyle(delta);
  const label = isNew ? t('history.new_pattern') : formatDelta(delta);

  return (
    <View
      style={[styles.badge, { backgroundColor: style.bg }]}
      accessibilityElementsHidden={true}
      importantForAccessibility="no-hide-descendants"
    >
      <Text style={[styles.text, { color: style.text }]}>{label}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 9999,
  },
  text: {
    fontSize: 12,
    fontWeight: '700',
  },
});
